import type { JSX } from 'react';
import { MainContent } from '../shared-mui-components/MainContent.tsx';
import { MaterialHeader } from '../layout/components/MaterialHeader.tsx';
import { MaterialTitle } from '../layout/components/MaterialTitle.tsx';
import {
    CheckCircleOutlined,
    DesignServices,
    RemoveCircleOutlineOutlined,
    Schedule,
} from '@mui/icons-material';
import { Localized, useLocalize } from 'dg-web-shared/lib/Localization.tsx';
import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
} from '@mui/material';
import { useServerFetch } from 'dg-web-shared/lib/hooks/ServerStateHooks.ts';
import { LoadingSpinnerPresets } from 'dg-web-shared/common/components/material-ui/PresetLoadingSpinner.tsx';
import { OperatorAsyncLoadedSection } from '../app/components/OperatorAsyncLoadedSection.tsx';
import { Outlet, useNavigate, useOutletContext } from 'react-router';
import { Formatter } from 'dg-web-shared/lib/Date.ts';
import { DateTime } from 'luxon';
import { EmDash } from 'dg-web-shared/lib/Punctuation.ts';
import { useLanguage } from 'dg-web-shared/lib/Localization.tsx';
import { PurchaseVisibility } from 'product-shared/product-template/ProductTemplateEnums.ts';
import { ProductTemplateConfig } from 'product-shared/product-template/ProductTemplate.ts';
import { EmptyResults } from '../shared-mui-components/ListMessages.tsx';
import { SeparationBar } from '../layout/components/SeparationBar.tsx';
import { useCaseNameFromConfigType } from 'product-shared/product-template/ProductTemplateUseCase.ts';
import { ZoneType } from 'dg-web-shared/dto/ZoneType.ts';
import { Message } from 'dg-web-shared/lib/Localized.ts';

export interface ProductTemplateListItem {
    contractTemplateId: number;
    productTemplateConfig: ProductTemplateConfig;
    appPurchaseVisibility: PurchaseVisibility | null;
    validityStart: string | null;
    validityEnd: string | null;
    zoneDescriptions: string[];
    roleNames: Message[] | null;
    dtaAccountDescription: string;
}

interface ProductTemplateResult {
    productTemplateList: ProductTemplateListItem[];
    showDtaAccount: boolean;
}

export function OperatorProductTypes() {
    const { language } = useLanguage();
    const [productTemplateResultState] = useServerFetch<
        ProductTemplateResult,
        { lang: string },
        null
    >(
        ({ lang }) => ({
            url: `/ui-api/operator-account/parkingabo/product-template-list/${lang}`,
        }),
        { lang: language },
    );

    return (
        <MainContent>
            <MaterialHeader editMode={false}>
                <MaterialTitle
                    Icon={DesignServices}
                    title={
                        <Localized
                            de="Produkttypen"
                            fr="Types de produits"
                            it="Tipi di prodotto"
                            en="Product types"
                        />
                    }
                />
            </MaterialHeader>
            <SeparationBar />
            <OperatorAsyncLoadedSection
                requestState={productTemplateResultState}
                pendingLoaderPreset={
                    LoadingSpinnerPresets.FillAllSpaceAndCenter
                }
                render={productTemplateResult => {
                    if (
                        productTemplateResult.productTemplateList.length === 0
                    ) {
                        return <EmptyResults hasFilter={false} />;
                    }
                    return (
                        <TableContainer>
                            <Table stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>
                                            <Localized
                                                de="Bezeichnung"
                                                fr="Description"
                                                it="Descrizione"
                                                en="Description"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Localized
                                                de="Use case"
                                                fr="Cas d'utilisation"
                                                it="Caso d'uso"
                                                en="Use case"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Localized
                                                de="App-Kauf"
                                                fr="Achat dans l'app"
                                                it="Acquisto nell'app"
                                                en="App purchase"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Localized
                                                de="Zonen/Parkings"
                                                fr="zones/parkings"
                                                it="zone/parcheggi"
                                                en="Zones/parkings"
                                            />
                                        </TableCell>
                                        {productTemplateResult.showDtaAccount && (
                                            <TableCell>
                                                <Localized
                                                    de="Gruppe"
                                                    fr="Groupe"
                                                    it="Gruppo"
                                                    en="Group"
                                                />
                                            </TableCell>
                                        )}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {productTemplateResult.productTemplateList.map(
                                        productTemplate => (
                                            <OperatorProductTypeRow
                                                key={
                                                    productTemplate.contractTemplateId
                                                }
                                                productTemplateListItem={
                                                    productTemplate
                                                }
                                                showDtaAccount={
                                                    productTemplateResult.showDtaAccount
                                                }
                                            />
                                        ),
                                    )}
                                </TableBody>
                            </Table>
                            <ProductTemplateOutlet
                                context={{
                                    showDtaAccount:
                                        productTemplateResult.showDtaAccount,
                                }}
                            />
                        </TableContainer>
                    );
                }}
            />
        </MainContent>
    );
}

function OperatorProductTypeRow({
    productTemplateListItem,
    showDtaAccount,
}: {
    productTemplateListItem: ProductTemplateListItem;
    showDtaAccount: boolean;
}) {
    const navigate = useNavigate();
    const configType = productTemplateListItem.productTemplateConfig.configType;
    return (
        <TableRow
            hover
            sx={{ cursor: 'pointer' }}
            onClick={() =>
                navigate(`${productTemplateListItem.contractTemplateId}/detail`)
            }
        >
            <TableCell>
                <Localized
                    {...productTemplateListItem.productTemplateConfig
                        .productName}
                />
            </TableCell>
            <TableCell>
                <Localized {...useCaseNameFromConfigType(configType)} />
            </TableCell>
            <TableCell>
                <AppStatusIconWithHint
                    appPurchaseVisibility={
                        productTemplateListItem.appPurchaseVisibility
                    }
                    validityStart={productTemplateListItem.validityStart}
                    validityEnd={productTemplateListItem.validityEnd}
                />
            </TableCell>
            <TableCell>
                <OperatorProductTypeZoneNameShort
                    zoneDescriptions={productTemplateListItem.zoneDescriptions}
                    allowedZoneTypes={
                        productTemplateListItem.productTemplateConfig
                            .allowedZoneTypes
                    }
                />
            </TableCell>
            {showDtaAccount && (
                <TableCell>
                    {productTemplateListItem.dtaAccountDescription}
                </TableCell>
            )}
        </TableRow>
    );
}

export function useProductTemplateOutlet() {
    return useOutletContext<ProductTemplateContext>();
}

function ProductTemplateOutlet({
    context,
}: {
    context: ProductTemplateContext;
}) {
    return <Outlet context={context} />;
}

interface ProductTemplateContext {
    showDtaAccount: boolean;
}

function AppStatusIconWithHint({
    appPurchaseVisibility,
    validityStart,
    validityEnd,
}: {
    appPurchaseVisibility: PurchaseVisibility | null;
    validityStart: string | null;
    validityEnd: string | null;
}): JSX.Element {
    const localize = useLocalize();
    return (
        <Tooltip
            title={localize(
                productTypeAppStatusText(
                    appPurchaseVisibility,
                    validityStart,
                    validityEnd,
                ),
            )}
            placement="right"
        >
            <ProductTypeAppStatusIcon
                appPurchaseVisibility={appPurchaseVisibility}
                validityStart={validityStart}
                validityEnd={validityEnd}
            />
        </Tooltip>
    );
}

export function ProductTypeAppStatusIcon({
    appPurchaseVisibility,
    validityStart,
    validityEnd,
}: {
    appPurchaseVisibility: PurchaseVisibility | null;
    validityStart: string | null;
    validityEnd: string | null;
}): JSX.Element {
    const isBookable =
        (validityStart == null ||
            DateTime.fromISO(validityStart) <= DateTime.now()) &&
        (validityEnd == null ||
            DateTime.fromISO(validityEnd) >= DateTime.now());

    switch (appPurchaseVisibility) {
        case PurchaseVisibility.VISIBLE:
            return (
                <CheckCircleOutlined
                    color={isBookable ? 'success' : 'warning'}
                />
            );
        case PurchaseVisibility.HIDDEN:
            return <Schedule color="warning" />;
        case null:
            return <RemoveCircleOutlineOutlined color="disabled" />;
    }
}

export function productTypeAppStatusText(
    appPurchaseVisibility: PurchaseVisibility | null,
    validityStart: string | null,
    validityEnd: string | null,
) {
    const validityStartFormatted = validityStart
        ? Formatter.dayMonthYear(DateTime.fromISO(validityStart))
        : null;

    const validityEndFormatted = validityEnd
        ? Formatter.dayMonthYear(DateTime.fromISO(validityEnd))
        : null;

    const isBookable =
        (validityStart == null ||
            DateTime.fromISO(validityStart) <= DateTime.now()) &&
        (validityEnd == null ||
            DateTime.fromISO(validityEnd) >= DateTime.now());

    const visibleStateText = isBookable
        ? {
              de: 'Verfügbar',
              fr: 'Disponible',
              it: 'Disponibile',
              en: 'Available',
          }
        : {
              de: 'Sichtbar',
              fr: 'Visible',
              it: 'Visibile',
              en: 'Visible',
          };

    switch (appPurchaseVisibility) {
        case PurchaseVisibility.VISIBLE:
            if (
                validityStartFormatted != null &&
                validityEndFormatted != null
            ) {
                return {
                    de: `${visibleStateText.de} (buchbar ab ${validityStartFormatted} bis ${validityEndFormatted})`,
                    fr: `${visibleStateText.fr} (réservable du ${validityStartFormatted} jusqu’au ${validityEndFormatted})`,
                    it: `${visibleStateText.it} (prenotabile dal ${validityStartFormatted} fino al ${validityEndFormatted})`,
                    en: `${visibleStateText.en} (bookable from ${validityStartFormatted} until ${validityEndFormatted})`,
                };
            }
            if (validityStartFormatted != null) {
                return {
                    de: `${visibleStateText.de} (erster buchbarer Tag: ${validityStartFormatted})`,
                    fr: `${visibleStateText.fr} (premier jour réservable: ${validityStartFormatted})`,
                    it: `${visibleStateText.it} (primo giorno prenotabile: ${validityStartFormatted})`,
                    en: `${visibleStateText.en} (first bookable day: ${validityStartFormatted})`,
                };
            }
            if (validityEndFormatted != null) {
                return {
                    de: `${visibleStateText.de} (letzter buchbarer Tag: ${validityEndFormatted})`,
                    fr: `${visibleStateText.fr} (dernier jour réservable: ${validityEndFormatted})`,
                    it: `${visibleStateText.it} (ultimo giorno prenotabile: ${validityEndFormatted})`,
                    en: `${visibleStateText.en} (last bookable day: ${validityEndFormatted})`,
                };
            }
            return visibleStateText;
        case PurchaseVisibility.HIDDEN:
            return {
                de: 'Noch nicht sichtbar',
                fr: 'Pas encore visible',
                it: 'Non ancora visibile',
                en: 'Not yet visible',
            };
        case null:
            return {
                de: 'Nicht möglich',
                fr: 'Pas possible',
                it: 'Non possilbe',
                en: 'Not possible',
            };
    }
}

function OperatorProductTypeZoneNameShort({
    zoneDescriptions,
    allowedZoneTypes,
}: {
    zoneDescriptions: string[];
    allowedZoneTypes: ZoneType[] | null;
}) {
    if (zoneDescriptions.length === 0 || allowedZoneTypes === null)
        return <Box>{EmDash}</Box>;
    if (zoneDescriptions.length === 1) {
        return <Box>{zoneDescriptions[0]}</Box>;
    }
    if (allowedZoneTypes.some(a => a === ZoneType.barrier_gate)) {
        return (
            <Localized
                de={`${zoneDescriptions.length} Parkings`}
                fr={`${zoneDescriptions.length} parkings`}
                it={`${zoneDescriptions.length} parcheggi`}
                en={`${zoneDescriptions.length} parkings`}
            />
        );
    }
    return (
        <Localized
            de={`${zoneDescriptions.length} Zonen`}
            fr={`${zoneDescriptions.length} zones`}
            it={`${zoneDescriptions.length} zone`}
            en={`${zoneDescriptions.length} zones`}
        />
    );
}
